import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Science as ScienceIcon,
} from '@mui/icons-material';
import { Lab, CreateLabDto, UpdateLabDto, LineMode } from '../../../types/lab';
import { labsService } from '../../../services/labsService';
import { DialogHeader, AddressSection, SageDetailsSection, buildSageDetails, EMPTY_SAGE_FIELDS, mapSageDetailsToFormFields, mapServerErrorsToFormErrors, createFieldChangeHandler, AdditionalEmailsEditor, validateAdditionalEmails, cleanAdditionalEmails } from '../../Common';
import type { SageFormFields } from '../../Common';
import { useSnackbar } from 'notistack';
import { validateEmail } from '../../../utils/validation';
import LabComboMappingsEditor from './LabComboMappingsEditor';
import { sageSupplierLinkingService } from '../../../services/sageSupplierLinkingService';
import type { SageSupplierDetails } from '../../../types/sage';
import { SageLinkSupplierDialog } from '../../Common';
import { sageTaxTypeService } from '../../../services/sageTaxTypeService';
import type { SageTaxType } from '../../../types/sageTaxType';

interface LabDialogProps {
  open: boolean;
  lab: Lab | null;
  onClose: (saved: boolean) => void;
  onCreated?: (lab: Lab) => void;
  initialTab?: number;
}

type LabFormData = {
  labName: string;
  contactNumber: string;
  emailAddress: string;
  additionalEmails: string[];
  defaultPoLineMode: LineMode;
  invoicingMode: string;
  defaultSageTaxTypeId: number | null;
} & SageFormFields;

const EMPTY_FORM: LabFormData = {
  labName: '',
  contactNumber: '',
  emailAddress: '',
  additionalEmails: [],
  defaultPoLineMode: 'Individual',
  invoicingMode: 'Standard',
  defaultSageTaxTypeId: null,
  ...EMPTY_SAGE_FIELDS,
};

const LabDialog: React.FC<LabDialogProps> = ({ open, lab, onClose, onCreated, initialTab }) => {
  const [formData, setFormData] = useState<LabFormData>(EMPTY_FORM);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const [taxTypes, setTaxTypes] = useState<SageTaxType[]>([]);
  const [findingInSage, setFindingInSage] = useState(false);
  const [sageMatch, setSageMatch] = useState<SageSupplierDetails | null>(null);

  useEffect(() => {
    if (open && lab) {
      sageTaxTypeService.getAllLocal().then(res => setTaxTypes(res.data ?? [])).catch(() => {});
    }
  }, [open, lab]);

  useEffect(() => {
    if (lab) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync form state from props when the dialog opens or its target changes; established dialog pattern
      setFormData({
        labName: lab.labName,
        contactNumber: lab.contactNumber || '',
        emailAddress: lab.emailAddress || '',
        additionalEmails: lab.additionalEmails ? [...lab.additionalEmails] : [],
        defaultPoLineMode: lab.defaultPoLineMode || 'Individual',
        invoicingMode: lab.invoicingMode || 'Standard',
        defaultSageTaxTypeId: lab.defaultSageTaxTypeId ?? null,
        ...mapSageDetailsToFormFields(lab.sageDetails),
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
    setActiveTab(initialTab ?? 0);
  }, [lab, open, initialTab]);

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.labName.trim()) {
      newErrors.labName = 'Lab name is required';
    } else if (formData.labName.trim().length < 2) {
      newErrors.labName = 'Lab name must be at least 2 characters';
    } else if (formData.labName.trim().length > 100) {
      newErrors.labName = 'Lab name must be less than 100 characters';
    }

    if (formData.emailAddress && !validateEmail(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }

    // Additional emails: each non-blank must be valid, unique, and not equal the primary.
    const additionalEmailsError = validateAdditionalEmails(formData.additionalEmails, formData.emailAddress);
    if (additionalEmailsError) {
      newErrors.additionalEmails = additionalEmailsError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      setActiveTab(0);
      return;
    }

    setLoading(true);
    try {
      const sageDetails = buildSageDetails(formData);
      const cleanedAdditionalEmails = cleanAdditionalEmails(formData.additionalEmails);

      if (lab) {
        const updateDto: UpdateLabDto = {
          labName: formData.labName,
          contactNumber: formData.contactNumber || undefined,
          emailAddress: formData.emailAddress || undefined,
          additionalEmails: cleanedAdditionalEmails,
          defaultPoLineMode: formData.defaultPoLineMode,
          invoicingMode: formData.invoicingMode,
          defaultSageTaxTypeId: formData.defaultSageTaxTypeId,
          sageDetails,
        };
        await labsService.update(lab.idLab, updateDto);
        enqueueSnackbar('Lab updated successfully', { variant: 'success' });
      } else {
        const createDto: CreateLabDto = {
          labName: formData.labName,
          contactNumber: formData.contactNumber || undefined,
          emailAddress: formData.emailAddress || undefined,
          additionalEmails: cleanedAdditionalEmails,
          sageDetails,
        };
        const created = await labsService.create(createDto);
        enqueueSnackbar('Lab created successfully', { variant: 'success' });
        onClose(true);
        onCreated?.(created);
        return;
      }
      onClose(true);
    } catch (error: unknown) {
      mapServerErrorsToFormErrors(error, setErrors, enqueueSnackbar, 'lab');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose(false);
    }
  };

  const handleFieldChange = createFieldChangeHandler<LabFormData>(setFormData, setErrors);

  const clearAdditionalEmailsError = () =>
    setErrors((prev) => {
      if (!prev.additionalEmails) return prev;
      const { additionalEmails: _omit, ...rest } = prev;
      return rest;
    });

  const handleAdditionalEmailsChange = (next: string[]) => {
    setFormData((prev) => ({ ...prev, additionalEmails: next }));
  };

  const handleFindInSage = async () => {
    if (!lab) return;
    setFindingInSage(true);
    try {
      const match = await sageSupplierLinkingService.findExistingSupplier('Lab', lab.idLab, formData.sageName?.trim());
      if (match) {
        setSageMatch(match);
      } else {
        enqueueSnackbar('No matching supplier found in Sage', { variant: 'info' });
      }
    } catch {
      enqueueSnackbar('Failed to search for supplier in Sage', { variant: 'error' });
    } finally {
      setFindingInSage(false);
    }
  };

  const suggestedSageName = formData.labName.trim();
  const isSageReady = !!formData.sageName?.trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 2 },
        },
      }}
    >
      <DialogHeader
        title={lab ? 'Edit Lab' : 'Add New Lab'}
        icon={<ScienceIcon sx={{ color: 'primary.main' }} />}
        onClose={handleClose}
        disabled={loading}
      />

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        aria-label="Lab form tabs"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 2,
          '& .MuiTab-root': { textTransform: 'none' },
        }}
      >
        <Tab label="Basic Info" id="lab-tab-basic" aria-controls="lab-tabpanel-basic" />
        <Tab label="Addresses" id="lab-tab-address" aria-controls="lab-tabpanel-address" />
        <Tab label="Sage" id="lab-tab-sage" aria-controls="lab-tabpanel-sage" />
      </Tabs>

      <DialogContent sx={{ pt: 3, minHeight: 300 }}>
        {/* Tab 0: Basic Info */}
        {activeTab === 0 && (
          <Box role="tabpanel" id="lab-tabpanel-basic" aria-labelledby="lab-tab-basic" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Lab Name"
              fullWidth
              value={formData.labName}
              onChange={(e) => handleFieldChange('labName', e.target.value)}
              error={!!errors.labName}
              helperText={errors.labName || 'Enter the name of the lab'}
              disabled={loading}
              autoFocus
              required
              slotProps={{ input: { sx: { borderRadius: 2 } } }}
            />

            <TextField
              label="Contact Number"
              fullWidth
              value={formData.contactNumber}
              onChange={(e) => handleFieldChange('contactNumber', e.target.value)}
              error={!!errors.contactNumber}
              helperText={errors.contactNumber || 'Enter the lab\'s phone number (optional)'}
              disabled={loading}
              slotProps={{ input: { sx: { borderRadius: 2 } } }}
            />

            <TextField
              label="Email Address"
              fullWidth
              type="email"
              value={formData.emailAddress}
              onChange={(e) => handleFieldChange('emailAddress', e.target.value)}
              error={!!errors.emailAddress}
              helperText={errors.emailAddress || 'Enter the lab\'s email address (optional)'}
              disabled={loading}
              slotProps={{ input: { sx: { borderRadius: 2 } } }}
            />

            <AdditionalEmailsEditor
              value={formData.additionalEmails}
              onChange={handleAdditionalEmailsChange}
              error={errors.additionalEmails}
              onClearError={clearAdditionalEmailsError}
              disabled={loading}
              description="Optional extra recipients (e.g. orders desk, accounts inbox) offered when sending purchase orders, invoices and credit notes."
            />

            {lab && (
              <Box sx={(theme) => ({
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                ...theme.applyStyles('dark', { backgroundColor: 'grey.800' }),
              })}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Lab ID: #{lab.idLab}
                </Typography>
                {lab.createdAt && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Created: {new Date(lab.createdAt).toLocaleString()}
                  </Typography>
                )}
                {lab.updatedAt && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Last Updated: {new Date(lab.updatedAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Tab 1: Addresses */}
        {activeTab === 1 && (
          <Box role="tabpanel" id="lab-tabpanel-address" aria-labelledby="lab-tab-address">
            <AddressSection
              postalAddress01={formData.postalAddress01}
              postalAddress02={formData.postalAddress02}
              postalAddress03={formData.postalAddress03}
              postalAddress04={formData.postalAddress04}
              postalAddress05={formData.postalAddress05}
              deliveryAddress01={formData.deliveryAddress01}
              deliveryAddress02={formData.deliveryAddress02}
              deliveryAddress03={formData.deliveryAddress03}
              deliveryAddress04={formData.deliveryAddress04}
              deliveryAddress05={formData.deliveryAddress05}
              onChange={(field, value) => handleFieldChange(field as keyof LabFormData, value)}
              disabled={loading}
            />
          </Box>
        )}

        {/* Tab 2: Sage */}
        {activeTab === 2 && (
          <Box role="tabpanel" id="lab-tabpanel-sage" aria-labelledby="lab-tab-sage" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <SageDetailsSection
              sageName={formData.sageName}
              taxReference={formData.taxReference}
              suggestedSageName={suggestedSageName}
              isSageReady={isSageReady}
              onSageNameChange={(v) => handleFieldChange('sageName', v)}
              onTaxReferenceChange={(v) => handleFieldChange('taxReference', v)}
              disabled={loading}
              isLinkedToSage={lab?.sageDetails?.isLinkedToSage}
              isCustomer={lab?.sageDetails?.isCustomer}
              isSupplier={lab?.sageDetails?.isSupplier}
              errors={{
                sageName: errors.sageName,
                taxReference: errors.taxReference,
              }}
              entityType="Lab"
              entityId={lab?.idLab}
              onFindInSage={lab ? handleFindInSage : undefined}
              findInSageLoading={findingInSage}
            />

            {lab && (
              <>
                <Divider />
                <FormControl fullWidth size="small">
                  <InputLabel id="default-po-line-mode-label">Default PO Line Mode</InputLabel>
                  <Select
                    labelId="default-po-line-mode-label"
                    value={formData.defaultPoLineMode}
                    onChange={(e) => handleFieldChange('defaultPoLineMode', e.target.value as LineMode)}
                    label="Default PO Line Mode"
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="Individual">Individual</MenuItem>
                    <MenuItem value="Combo">Combo</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Invoicing Mode</InputLabel>
                  <Select
                    value={formData.invoicingMode}
                    onChange={(e) => handleFieldChange('invoicingMode', e.target.value)}
                    label="Invoicing Mode"
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="Standard">Standard (per workflow)</MenuItem>
                    <MenuItem value="Consolidated">Consolidated</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                  <InputLabel>Default Tax Type</InputLabel>
                  <Select
                    value={formData.defaultSageTaxTypeId == null ? '' : String(formData.defaultSageTaxTypeId)}
                    onChange={(e) => handleFieldChange('defaultSageTaxTypeId', e.target.value === '' ? null : Number(e.target.value))}
                    label="Default Tax Type"
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">Use global default</MenuItem>
                    {taxTypes.filter(t => t.active).map(t => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name} ({t.percentage}%)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <LabComboMappingsEditor labId={lab.idLab} disabled={loading} />
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ textTransform: 'none', borderRadius: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.labName.trim()}
          sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            lab ? 'Update' : 'Create'
          )}
        </Button>
      </DialogActions>

      {sageMatch && lab && (
        <SageLinkSupplierDialog
          open
          supplierName={sageMatch.name}
          sageSupplierId={sageMatch.id}
          entityType="Lab"
          entityId={lab.idLab}
          onClose={() => setSageMatch(null)}
          onLinked={() => {
            setSageMatch(null);
            enqueueSnackbar('Supplier linked successfully', { variant: 'success' });
            onClose(true);
          }}
        />
      )}
    </Dialog>
  );
};

export default LabDialog;
